import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'
import { ModalType, ModalPropsMap, ModalOptions, ModalStackEntry } from 'types/modals'
import { ModalRegistry } from './ModalRegistry'

interface ModalContextType {
  stack: ModalStackEntry[]
  isOpen: boolean
  currentModal: ModalStackEntry | null
  push: <T extends ModalType>(type: T, props: ModalPropsMap[T], options?: ModalOptions) => string
  pop: () => void
  close: () => void
  closeAll: () => void
  replace: <T extends ModalType>(type: T, props: ModalPropsMap[T], options?: ModalOptions) => string
  // Legacy compatibility
  open: (component: JSX.Element) => void
  toggle: () => void
  modal: JSX.Element | null
}

const ModalContext = createContext<ModalContextType | null>(null)

let modalIdCounter = 0
const generateModalId = () => `modal-${++modalIdCounter}-${Date.now()}`

export const ModalProvider = ({ children }: PropsWithChildren) => {
  const [stack, setStack] = useState<ModalStackEntry[]>([])

  const isOpen = stack.length > 0
  const currentModal = stack[stack.length - 1] ?? null

  const push = useCallback(
    <T extends ModalType>(type: T, props: ModalPropsMap[T], options: ModalOptions = {}): string => {
      const id = generateModalId()
      const entry: ModalStackEntry<T> = { id, type, props, options }
      setStack((prev) => [...prev, entry as ModalStackEntry])
      return id
    },
    [],
  )

  const pop = useCallback(() => {
    setStack((prev) => {
      if (prev.length === 0) return prev
      const removed = prev[prev.length - 1]
      removed.options.onClose?.()
      return prev.slice(0, -1)
    })
  }, [])

  const closeAll = useCallback(() => {
    setStack((prev) => {
      prev.forEach((entry) => entry.options.onClose?.())
      return []
    })
  }, [])

  const replace = useCallback(
    <T extends ModalType>(type: T, props: ModalPropsMap[T], options: ModalOptions = {}): string => {
      const id = generateModalId()
      const entry: ModalStackEntry<T> = { id, type, props, options }
      setStack((prev) => {
        if (prev.length === 0) return [entry as ModalStackEntry]
        const removed = prev[prev.length - 1]
        removed.options.onClose?.()
        return [...prev.slice(0, -1), entry as ModalStackEntry]
      })
      return id
    },
    [],
  )

  // Legacy compatibility
  const open = useCallback(
    (component: JSX.Element) => {
      push(ModalType.CUSTOM, { component } as ModalPropsMap[ModalType.CUSTOM])
    },
    [push],
  )

  const toggle = useCallback(() => {
    if (isOpen) pop()
  }, [isOpen, pop])

  const contextValue = useMemo<ModalContextType>(
    () => ({
      stack,
      isOpen,
      currentModal,
      push,
      pop,
      close: pop,
      closeAll,
      replace,
      open,
      toggle,
      modal: null,
    }),
    [stack, isOpen, currentModal, push, pop, closeAll, replace, open, toggle],
  )

  return <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>
}

export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error("useModal deve essere usato all'interno di un ModalProvider")
  }
  return context
}

export const useModalActions = () => {
  const { push, replace, pop, closeAll } = useModal()

  return {
    showLogin: (props: ModalPropsMap[ModalType.LOGIN] = {}, options?: ModalOptions) =>
      push(ModalType.LOGIN, props, options),

    showRegister: (props: ModalPropsMap[ModalType.REGISTER] = {}, options?: ModalOptions) =>
      push(ModalType.REGISTER, props, options),

    showResetPassword: (props: ModalPropsMap[ModalType.RESET_PASSWORD] = {}, options?: ModalOptions) =>
      push(ModalType.RESET_PASSWORD, props, options),

    showOpenTicket: (options?: ModalOptions) =>
      push(ModalType.OPEN_TICKET, {} as ModalPropsMap[ModalType.OPEN_TICKET], options),

    showCloseTicket: (props: ModalPropsMap[ModalType.CLOSE_TICKET], options?: ModalOptions) =>
      push(ModalType.CLOSE_TICKET, props, options),

    showOpenChat: (options?: ModalOptions) =>
      push(ModalType.OPEN_CHAT, {} as ModalPropsMap[ModalType.OPEN_CHAT], options),

    showExportChat: (props: ModalPropsMap[ModalType.EXPORT_CHAT] = {}, options?: ModalOptions) =>
      push(ModalType.EXPORT_CHAT, props, options),

    showCookiePreferences: (options?: ModalOptions) =>
      push(ModalType.COOKIE_PREFERENCES, {} as ModalPropsMap[ModalType.COOKIE_PREFERENCES], options),

    goBack: pop,
    close: pop,
    closeAll,

    replaceWithRegister: (props: ModalPropsMap[ModalType.REGISTER] = {}, options?: ModalOptions) =>
      replace(ModalType.REGISTER, props, { ...options, showBackButton: true }),

    replaceWithLogin: (props: ModalPropsMap[ModalType.LOGIN] = {}, options?: ModalOptions) =>
      replace(ModalType.LOGIN, props, { ...options, showBackButton: true }),

    replaceWithResetPassword: (props: ModalPropsMap[ModalType.RESET_PASSWORD] = {}, options?: ModalOptions) =>
      replace(ModalType.RESET_PASSWORD, props, { ...options, showBackButton: true }),
  }
}

type ModalOutletProps = {
  /** 'sheet' = widget bottom sheet (default), 'dialog' = centered fullscreen card for the chat page. */
  variant?: 'sheet' | 'dialog'
}

export const ModalOutlet = ({ variant = 'sheet' }: ModalOutletProps) => {
  const { isOpen, currentModal, pop } = useModal()

  const handleOverlayClick = useCallback(() => {
    if (currentModal?.options.dismissable !== false) {
      pop()
    }
  }, [currentModal, pop])

  const renderModal = () => {
    if (!currentModal) return null

    const ModalComponent = ModalRegistry[currentModal.type]
    if (!ModalComponent) {
      console.error(`Modal type ${currentModal.type} not found in registry`)
      return null
    }

    return <ModalComponent {...(currentModal.props as any)} />
  }

  if (!isOpen) return null

  if (variant === 'dialog') {
    return (
      <div
        className="animate-fade-in-medium tw-fixed tw-inset-0 tw-z-[998] tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-p-4 tw-backdrop-blur-sm"
        onClick={handleOverlayClick}
      >
        <div
          className="animate-scale-in tw-max-h-[90vh] tw-w-full tw-max-w-md tw-overflow-auto tw-rounded-2xl tw-bg-surface tw-shadow-2xl"
          style={{ border: '1px solid var(--page-border)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {renderModal()}
        </div>
      </div>
    )
  }

  return (
    <div
      className="animate-fade-in tw-absolute tw-inset-0 tw-z-[998] tw-bg-overlay tw-rounded-[16px] tw-overflow-hidden"
      onClick={handleOverlayClick}
    >
      <div className="tw-flex tw-items-end tw-justify-center tw-h-full" onClick={(e) => e.stopPropagation()}>
        <div className="animate-slide-up-modal tw-w-full tw-bg-surface tw-rounded-t-[16px] tw-max-h-[90%] tw-overflow-auto">
          {renderModal()}
        </div>
      </div>
    </div>
  )
}
